import { jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import analytics from "../analytics/analytics.js";
import { chatBotConfigFallback } from "../config/chatBotConfigFallback.js";
import { getDisplayConfig } from "../config/displayConfig.js";
import { getFeatureConfig } from "../config/featureConfig.js";
import { getPayloadConfig } from "../config/payloadConfig.js";
import { getServiceConfig } from "../config/serviceConfig.js";
import { fetchChatbotFeatures } from "../services/chatbotFeaturesService.js";
import { getHostingApp } from "../utils/getHostingApp.js";
const ConfigContext = /*#__PURE__*/ createContext({
    resolvedConfig: void 0,
    resolvedDisplay: void 0,
    resolvedFeatures: void 0,
    resolvedPayload: void 0,
    chatbotFeatures: void 0
});
const useConfig = ()=>{
    const context = useContext(ConfigContext);
    if (!context) throw new Error('useConfig must be used within a ConfigProvider');
    return context;
};
const ConfigProvider = ({ children, customerName, isChatBoxOpened, locale, startingChatMessagesPayload, viewType: viewTypeProp, webstats, logAnalytics })=>{
    const viewTypeFromWebApp = viewTypeProp || getHostingApp();
    const [resolvedConfig, setResolvedConfig] = useState(void 0);
    const [resolvedDisplay, setResolvedDisplay] = useState(void 0);
    const [resolvedFeatures, setResolvedFeatures] = useState(void 0);
    const [resolvedPayload, setResolvedPayload] = useState(void 0);
    const [chatbotFeatures, setChatbotFeatures] = useState(void 0);
    useEffect(()=>{
        let isMounted = true;
        const processConfig = async (viewTypeFromWebApp)=>{
            const isMobile = 'boolean' == typeof webstats?.isMobile ? webstats.isMobile : false;
            const payload = getPayloadConfig({
                isChatBoxOpened,
                customerName,
                locale,
                startingChatMessagesPayload,
                viewType: viewTypeFromWebApp,
                webstats
            });
            setResolvedPayload(payload);
            const serviceConfig = getServiceConfig(viewTypeFromWebApp, isMobile, customerName, logAnalytics);
            const displayConfig = getDisplayConfig(viewTypeFromWebApp, isMobile);
            const featuresConfig = getFeatureConfig(viewTypeFromWebApp, isChatBoxOpened);
            const fetchRemoteFeatures = async ()=>{
                const headers = serviceConfig.fetchRequestOptions?.headers ?? {};
                return fetchChatbotFeatures(headers);
            };
            try {
                const data = await fetchRemoteFeatures();
                if (!isMounted) return;
                setResolvedFeatures(featuresConfig);
                setResolvedConfig(serviceConfig);
                setResolvedDisplay(displayConfig);
                setChatbotFeatures(data ?? chatBotConfigFallback());
            } catch (error) {
                analytics.log({
                    label: 'chatbotFeaturesFetchError',
                    message: 'error in chatbotFeaturesFetchError',
                    error
                });
                if (!isMounted) return;
                setResolvedPayload(payload);
                setResolvedConfig(serviceConfig);
                setResolvedDisplay(displayConfig);
                setChatbotFeatures(chatBotConfigFallback());
                setResolvedFeatures(featuresConfig);
            }
        };
        setResolvedPayload(void 0);
        setResolvedConfig(void 0);
        setResolvedDisplay(void 0);
        setResolvedFeatures(void 0);
        setChatbotFeatures(void 0);
        processConfig(viewTypeFromWebApp).catch(()=>{
            if (!isMounted) return;
            const isMobile = 'boolean' == typeof webstats?.isMobile ? webstats.isMobile : false;
            const fallbackPayload = getPayloadConfig({
                isChatBoxOpened,
                customerName,
                locale,
                startingChatMessagesPayload,
                viewType: viewTypeFromWebApp,
                webstats
            });
            setResolvedPayload(fallbackPayload);
            setResolvedConfig(getServiceConfig(viewTypeFromWebApp, isMobile, customerName, logAnalytics));
            setResolvedDisplay(getDisplayConfig(viewTypeFromWebApp, isMobile));
            setResolvedFeatures(getFeatureConfig(viewTypeFromWebApp, isChatBoxOpened));
            setChatbotFeatures(chatBotConfigFallback());
        });
        return ()=>{
            isMounted = false;
        };
    }, [
        customerName,
        locale,
        logAnalytics,
        startingChatMessagesPayload,
        viewTypeFromWebApp,
        webstats
    ]);
    const value = useMemo(()=>({
            resolvedConfig,
            resolvedDisplay,
            resolvedFeatures,
            resolvedPayload,
            chatbotFeatures
        }), [
        resolvedConfig,
        resolvedDisplay,
        resolvedFeatures,
        resolvedPayload,
        chatbotFeatures
    ]);
    if (!resolvedConfig || !resolvedDisplay || !resolvedFeatures || !resolvedPayload || void 0 === chatbotFeatures) return null;
    return /*#__PURE__*/ jsx(ConfigContext.Provider, {
        value: value,
        children: 'function' == typeof children ? children(resolvedConfig, resolvedDisplay, resolvedFeatures, resolvedPayload, chatbotFeatures) : children
    });
};
export { ConfigContext, ConfigProvider, useConfig };
